import React, { useEffect, useState, useMemo } from "react";
import {
    CircularProgress, Button, TextField, InputAdornment, FormControl,
    InputLabel, Select, MenuItem, Chip, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Avatar,
    Paper, IconButton, Menu, Typography,
} from "@mui/material";
import SearchIcon    from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon     from "@mui/icons-material/Clear";
import AddIcon       from "@mui/icons-material/Add";
import MoreVertIcon  from "@mui/icons-material/MoreVert";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { useSnackbar } from "notistack";
import moment from "moment";
import * as XLSX from "xlsx";
import { get_students_api, suspend_student_api } from "../../DAL/FlightSchools/FlightSchools";
import AddStudentDialog from "./AddStudentDialog";
import ConfirmationPopup from "../../components/GeneralComponents/ConfirmationPopup";
import StudentDetailDrawer from "../../components/StudentDetailDrawer";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
        "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
        "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
        "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a" },
};

const selectSx = {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    "& .MuiSvgIcon-root": { color: "#c01e2e" },
};

const thSx = {
    color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase",
    letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif",
    borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4",
};

export default function FlightSchoolStudents({ schoolId, school, onUpdate }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading,     setIsLoading]     = useState(true);
    const [students,      setStudents]      = useState([]);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, student: null });
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [drawerStudent,    setDrawerStudent]    = useState(null);
    const [menuAnchor,    setMenuAnchor]    = useState(null);
    const [menuStudent,   setMenuStudent]   = useState(null);
    const [searchQuery,   setSearchQuery]   = useState("");
    const [statusFilter,  setStatusFilter]  = useState("all");
    const [page,          setPage]          = useState(0);
    const [rowsPerPage,   setRowsPerPage]   = useState(25);

    useEffect(() => { loadStudents(); }, [schoolId]);

    const loadStudents = async () => {
        try {
            const r = await get_students_api(schoolId);
            if (r.code === 200) setStudents(r.students || []);
            else enqueueSnackbar(r.message || "Failed to load students", { variant: "error" });
        } catch { enqueueSnackbar("Error loading students", { variant: "error" }); }
        finally { setIsLoading(false); }
    };

    const filteredData = useMemo(() => students.filter((s) => {
        const q = searchQuery.toLowerCase();
        const name = `${s.first_name} ${s.last_name}`.toLowerCase();
        const matchesSearch = !searchQuery || name.includes(q) || s.email?.toLowerCase().includes(q);
        const matchesStatus = statusFilter === "all" || s.status === statusFilter;
        return matchesSearch && matchesStatus;
    }), [students, searchQuery, statusFilter]);

    const getStatus = (s) => {
        if (s.status === "suspended") return { label: "Suspended", bg: "rgba(217,119,6,0.1)", color: "#d97706" };
        if (s.status === "pending")   return { label: "Pending Invite", bg: "rgba(37,99,235,0.1)", color: "#2563eb" };
        return { label: "Active", bg: "rgba(22,163,74,0.1)", color: "#16a34a" };
    };

    const handleSuspend = async (reason) => {
        const student = confirmDialog.student;
        try {
            const r = await suspend_student_api(schoolId, student._id, reason);
            if (r.code === 200) { enqueueSnackbar("Student suspended", { variant: "success" }); loadStudents(); onUpdate?.(); }
            else enqueueSnackbar(r.message || "Failed to suspend", { variant: "error" });
        } catch { enqueueSnackbar("Error suspending student", { variant: "error" }); }
        setConfirmDialog({ open: false, action: null, student: null });
    };

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredData.map((s) => ({
            "First Name": s.first_name, "Last Name": s.last_name, Email: s.email,
            Status: getStatus(s).label, "Created At": s.createdAt ? moment(s.createdAt).format("DD-MM-YYYY HH:mm") : "",
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Students");
        XLSX.writeFile(wb, `${school.name}_students.xlsx`);
    };

    if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;

    return (
        <>
            {/* Header row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <TextField size="small" placeholder="Search students…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ ...inputSx, minWidth: 250 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#aaa", fontSize: 18 }} /></InputAdornment> }} />

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={selectSx}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                        </Select>
                    </FormControl>

                    {(searchQuery || statusFilter !== "all") && (
                        <Button variant="outlined" size="small" onClick={() => { setSearchQuery(""); setStatusFilter("all"); }} startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                            sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px",
                                "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.05)" } }}>
                            Clear
                        </Button>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FilterListIcon sx={{ color: "#aaa", fontSize: 16 }} />
                        <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                            {filteredData.length} of {students.length}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={exportToExcel}
                        sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px",
                            "&:hover": { borderColor: "#c01e2e", color: "#c01e2e" } }}>
                        Export
                    </Button>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}
                        sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
                        Add Student
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {["#", "Student", "Email", "Status", "Joined", ""].map((h) => (
                                <TableCell key={h} sx={{ ...thSx, ...(h === "" && { width: 48 }) }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((student, idx) => {
                            const st = getStatus(student);
                            return (
                                <TableRow key={student._id || idx} hover onClick={() => { setDrawerStudent(student); setDetailDrawerOpen(true); }}
                                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>
                                    <TableCell sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{page * rowsPerPage + idx + 1}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Avatar sx={{ width: 34, height: 34, backgroundColor: school?.branding?.primary_color || "#c01e2e", fontSize: "13px" }}>
                                                {student.first_name?.[0]}{student.last_name?.[0]}
                                            </Avatar>
                                            <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
                                                {student.first_name} {student.last_name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{student.email}</TableCell>
                                    <TableCell>
                                        <Chip label={st.label} size="small"
                                            sx={{ backgroundColor: st.bg, color: st.color, fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
                                    </TableCell>
                                    <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                        {student.createdAt ? moment(student.createdAt).format("DD MMM YYYY") : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuStudent(student); }}
                                            sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                                            <MoreVertIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination component="div" count={filteredData.length} page={page}
                onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ color: "#6b6b6b", fontFamily: "'Rubik', sans-serif",
                    "& .MuiTablePagination-selectIcon": { color: "#c01e2e" },
                    "& .MuiTablePagination-actions button": { color: "#6b6b6b" } }} />

            {/* Actions Menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuStudent(null); }}
                PaperProps={{ sx: { backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    "& .MuiMenuItem-root": { color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" },
                    "& .MuiMenuItem-root:hover": { backgroundColor: "#F7F4EF" } } }}>
                {menuStudent?.status !== "suspended" && (
                    <MenuItem onClick={() => { setConfirmDialog({ open: true, action: "suspend", student: menuStudent }); setMenuAnchor(null); setMenuStudent(null); }}>
                        <PauseCircleIcon sx={{ mr: 1, fontSize: 18, color: "#d97706" }} /> Suspend
                    </MenuItem>
                )}
            </Menu>

            <AddStudentDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} schoolId={schoolId} school={school} onSuccess={() => { loadStudents(); onUpdate?.(); setAddDialogOpen(false); }} />

            <ConfirmationPopup openDelete={confirmDialog.open} setOpenDelete={(open) => setConfirmDialog({ ...confirmDialog, open })} handleAgree={handleSuspend}
                title={`Are you sure you want to suspend ${confirmDialog.student?.first_name} ${confirmDialog.student?.last_name}?`}
                reasonField reasonRequired reasonLabel="Reason for suspension" />

            <StudentDetailDrawer open={detailDrawerOpen} onClose={() => { setDetailDrawerOpen(false); setDrawerStudent(null); }} student={drawerStudent} school={school} />
        </>
    );
}
